import React, { useRef, useEffect, useState, useMemo } from 'react'
import { Box } from '@mui/system'
import Styles from './OperatorUserActionList.module.css'
import OperatorUserActionSkeleton from './OperatorUserActionSkeleton'
import { IFButton, IFText } from 'Components'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  timelineItemClasses,
} from '@mui/lab'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined'
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined'
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote'
import ReportIcon from '@mui/icons-material/Report'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import { Virtuoso } from 'react-virtuoso'
import { Waypoint } from 'react-waypoint'
import { Popover } from '@mui/material'
import RequestState from 'Enums/RequestState'
import { Colors } from 'Theme'
import InfinityEnums from 'Enums/InfinityEnums'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { useOverlayScrollbars } from 'overlayscrollbars-react'
import 'overlayscrollbars/overlayscrollbars.css'

const OperatorUserActionList = ({
  fetchOperatorUserActionsRequestState,
  paginationOffset,
  operatorUserActions,
  loadMoreData,
  handleRetry,
  operatorUserActionsType,
  tablesHeight,
  operatorUser,
  clickedOperatorUser,
}) => {
  const { t } = useTranslation()
  const virtuosoRef = useRef(null)
  const [failedRequest, setFailedRequest] = useState(false)
  const options = {
    scrollbars: {
      visibility: 'auto',
      autoHide: 'move',
      autoHideDelay: 1000,
    },
  }
  const rootRef = useRef(null)
  const [scroller, setScroller] = useState(null)
  const [initialize, osInstance] = useOverlayScrollbars({
    options,
    defer: true,
  })
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [openPopoverIndex, setOpenPopoverIndex] = useState(null)
  const handlePopoverOpen = (event, index) => {
    setOpenPopoverIndex(index)
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
    setOpenPopoverIndex(null)
  }
  useEffect(() => {
    const { current: root } = rootRef
    if (scroller && root) {
      initialize({
        target: root,
        elements: {
          viewport: scroller,
        },
      })
    }
    return () => osInstance()?.destroy()
  }, [scroller, initialize, osInstance])

  const flattenedActions = useMemo(() => {
    if (operatorUserActionsType === InfinityEnums.Models.ChargePoint) {
      return operatorUserActions
        ?.map((action) => {
          return action.targets?.map((target) => {
            let userAction
            let subject
            let model = ''
            let phrase = ''
            let verb = ''
            let createdAt = moment(action.createdAt).format('MMM DD, YYYY LTS')
            let attributes = ''
            userAction =
              action.operatorUser?.email === operatorUser?.email
                ? t('ChargePointDetails.EditingOperatorUser')
                : action.operatorUser?.name

            subject = `${target.reference}`

            model = `${target.model}:`

            if (action.action === InfinityEnums.OperatorUserActionType.Edit) {
              verb = t('OperatorUserActions.Changed')

              subject = `${target.reference}'s`

              const fields = target.attributes || []
              const formattedFields = fields
                ?.map((field) => {
                  if (Array.isArray(field.newValue) || field.newValue === '') {
                    return field.name
                  }
                  if (typeof field.newValue === 'number') {
                    field.newValue = field.newValue.toFixed(2)
                  }
                  if (field.deletedValueReference) {
                    verb = t('OperatorUserActions.Removed')
                    phrase = `${userAction} ${verb} ${
                      field.deletedValueReference
                    } ${t('OperatorUserActions.from')} ${subject} ${field.name}`
                    return null
                  }
                  if (
                    typeof field.newValue === 'string' &&
                    /^[a-fA-F0-9]{24}$/.test(field.newValue)
                  ) {
                    const association = action.associations.find(
                      (item) =>
                        item.id.modelId.toString() ===
                        field.newValue.toString(),
                    )
                    let newValue = association.reference
                    return `${field.name} to ${newValue}`
                  }
                  if (
                    typeof field.newValue === 'object' &&
                    field.newValue !== null
                  ) {
                    let objectValue = JSON.stringify(field.newValue)
                    if (objectValue.length > 20) {
                      objectValue = objectValue.slice(0, 20) + '...'
                    }
                    return `${field.name} to ${objectValue}`
                  }
                  return `${field.name} to ${field.newValue}`
                })
                .join(', ')

              if (verb === t('OperatorUserActions.Changed')) {
                phrase = `${userAction} ${verb} ${
                  model ? model + ' ' : ''
                }${subject} ${formattedFields}`
              }

              return phrase
                ? {
                    phrase,
                    createdAt,
                    verb: action.action,
                  }
                : null
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Create
            ) {
              verb = t('OperatorUserActions.Created')

              attributes = target.attributes?.map((field) => {
                let newValue = field.newValue

                if (Array.isArray(newValue)) {
                  newValue = newValue.map((item) => {
                    if (
                      typeof item === 'string' &&
                      /^[a-fA-F0-9]{24}$/.test(item)
                    ) {
                      const association = action.associations.find(
                        (assoc) =>
                          assoc.id.modelId.toString() === item.toString(),
                      )
                      return association ? association.reference : item
                    }
                    return item
                  })
                } else if (typeof newValue === 'object' && newValue !== null) {
                  newValue = Object.entries(newValue).reduce(
                    (acc, [key, value]) => {
                      if (
                        typeof value === 'string' &&
                        /^[a-fA-F0-9]{24}$/.test(value)
                      ) {
                        const association = action.associations.find(
                          (assoc) =>
                            assoc.id.modelId.toString() === value.toString(),
                        )
                        acc[key] = association ? association.reference : value
                      } else {
                        acc[key] = value
                      }
                      return acc
                    },
                    {},
                  )
                } else if (
                  typeof newValue === 'string' &&
                  moment(newValue, moment.ISO_8601, true).isValid()
                ) {
                  newValue = moment(newValue).format('MMM DD, YYYY LTS')
                } else if (
                  newValue &&
                  typeof newValue === 'string' &&
                  /^[a-fA-F0-9]{24}$/.test(field.newValue) &&
                  action.associations
                ) {
                  const association = action.associations.find(
                    (item) =>
                      item.id.modelId.toString() === field.newValue.toString(),
                  )
                  if (association) {
                    newValue = association.reference
                  }
                }

                return {
                  name: field.name,
                  newValue: newValue,
                }
              })

              phrase = `${userAction} ${verb} ${
                model ? model + ' ' : ''
              }${subject}`
              return phrase
                ? {
                    phrase,
                    createdAt,
                    verb: action.action,
                    ...(action.action ===
                    InfinityEnums.OperatorUserActionType.Create
                      ? { attributes }
                      : {}),
                  }
                : null
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Delete
            ) {
              verb = t('OperatorUserActions.Deleted')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Deactivate
            ) {
              verb = t('OperatorUserActions.Deactivated')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Activate
            ) {
              verb = t('OperatorUserActions.Activated')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Move
            ) {
              verb = t('OperatorUserActions.Moved')
              const stationAssociation = action.associations?.find(
                (assoc) => assoc.model === InfinityEnums.Models.Station,
              )
              if (stationAssociation) {
                phrase = `${userAction} ${verb}  ${target.reference} ${t(
                  'OperatorUserActions.To',
                )} ${stationAssociation.model}: ${stationAssociation.reference}`
              } else {
                phrase = `${userAction} ${verb} ${
                  model ? model + ' ' : ''
                }${subject}`
              }
              return phrase
                ? {
                    phrase,
                    createdAt,
                    verb: action.action,
                  }
                : null
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.OCPP
            ) {
              const stationAssociation = action.associations?.find(
                (assoc) => assoc.model === InfinityEnums.Models.ChargePoint,
              )
              const stationTarget = action.targets?.find(
                (target) => target.model === InfinityEnums.Models.ChargePoint,
              )
              const ocppActionTypeKey = `${action.ocppActionType}`

              if (
                action.ocppActionType ===
                InfinityEnums.OperatorUserOcppActionType.ChangeConfiguration
              ) {
                verb = t('OperatorUserActions.Changed')
                if (stationAssociation?.attributes?.[0]) {
                  phrase = `${userAction} ${verb} ${
                    stationAssociation.attributes[0].name
                  } ${t('OperatorUserActions.To')} ${
                    stationAssociation.attributes[0].newValue
                  }`
                }
              } else {
                verb = t('OperatorUserActions.Preformed')
                phrase = `${userAction} ${verb} ${t(ocppActionTypeKey)} ${t(
                  'OperatorUserActions.On',
                )} ${
                  stationTarget?.reference ||
                  stationAssociation?.reference ||
                  ''
                }`
              }

              return phrase
                ? {
                    phrase,
                    createdAt,
                    details: action.details,
                    verb: action.action,
                  }
                : null
            }
            phrase = `${userAction} ${verb} ${
              model ? model + ' ' : ''
            }${subject}`
            return phrase
              ? {
                  phrase,
                  createdAt,
                  verb: action.action,
                  ...(action.action ===
                  InfinityEnums.OperatorUserActionType.Create
                    ? { attributes }
                    : {}),
                }
              : null
          })
        })
        .flat()
        .filter(Boolean)
    } else if (operatorUserActionsType === InfinityEnums.Models.ChargingToken) {
      return operatorUserActions
        ?.map((action) => {
          return action.targets?.map((target) => {
            let userAction
            let subject
            let model = ''
            let phrase = ''
            let verb = ''
            let createdAt = moment(action.createdAt).format('MMM DD, YYYY LTS')
            let attributes = ''

            userAction = t('ChargePointDetails.EditingOperatorUser')

            subject = `${target.reference}`
            if (target.model === InfinityEnums.Models.ChargingToken) {
              model = ''
            } else {
              model = `${target.model}:`
            }

            if (action.action === InfinityEnums.OperatorUserActionType.Edit) {
              verb = t('OperatorUserActions.Changed')

              subject = `${target.reference}'s`

              const fields = target.attributes || []
              const formattedFields = fields
                ?.map((field) => {
                  if (Array.isArray(field.newValue) || field.newValue === '') {
                    return field.name
                  }
                  if (typeof field.newValue === 'number') {
                    field.newValue = field.newValue.toFixed(2)
                  }
                  if (field.deletedValueReference) {
                    verb = t('OperatorUserActions.Removed')
                    phrase = `${userAction} ${verb} ${
                      field.deletedValueReference
                    } ${t('OperatorUserActions.from')} ${subject} ${field.name}`
                    return null
                  }
                  if (
                    typeof field.newValue === 'string' &&
                    /^[a-fA-F0-9]{24}$/.test(field.newValue)
                  ) {
                    const association = action.associations.find(
                      (item) =>
                        item.id.modelId.toString() ===
                        field.newValue.toString(),
                    )
                    let newValue = association.reference
                    return `${field.name} to ${newValue}`
                  }
                  return `${field.name} to ${field.newValue}`
                })
                .join(', ')

              if (verb === t('OperatorUserActions.Changed')) {
                phrase = `${userAction} ${verb} ${
                  model ? model + ' ' : ''
                }${subject} ${formattedFields}`
              }

              return phrase
                ? {
                    phrase,
                    createdAt,
                    verb: action.action,
                  }
                : null
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Create
            ) {
              verb = t('OperatorUserActions.Created')
              attributes = target.attributes?.map((field) => {
                let newValue = field.newValue

                if (Array.isArray(newValue)) {
                  newValue = newValue.map((item) => {
                    if (
                      typeof item === 'string' &&
                      /^[a-fA-F0-9]{24}$/.test(item)
                    ) {
                      const association = action.associations.find(
                        (assoc) =>
                          assoc.id.modelId.toString() === item.toString(),
                      )
                      return association ? association.reference : item
                    }
                    return item
                  })
                } else if (typeof newValue === 'object' && newValue !== null) {
                  newValue = Object.entries(newValue).reduce(
                    (acc, [key, value]) => {
                      if (
                        typeof value === 'string' &&
                        /^[a-fA-F0-9]{24}$/.test(value)
                      ) {
                        const association = action.associations.find(
                          (assoc) =>
                            assoc.id.modelId.toString() === value.toString(),
                        )
                        acc[key] = association ? association.reference : value
                      } else {
                        acc[key] = value
                      }
                      return acc
                    },
                    {},
                  )
                } else if (
                  typeof newValue === 'string' &&
                  moment(newValue, moment.ISO_8601, true).isValid()
                ) {
                  newValue = moment(newValue).format('MMM DD, YYYY LTS')
                } else if (
                  newValue &&
                  typeof newValue === 'string' &&
                  /^[a-fA-F0-9]{24}$/.test(field.newValue) &&
                  action.associations
                ) {
                  const association = action.associations.find(
                    (item) =>
                      item.id.modelId.toString() === field.newValue.toString(),
                  )
                  if (association) {
                    newValue = association.reference
                  }
                }

                return {
                  name: field.name,
                  newValue: newValue,
                }
              })
              phrase = `${userAction} ${verb} ${
                model ? model + ' ' : ''
              }${subject}`
              return phrase
                ? {
                    phrase,
                    createdAt,
                    verb: action.action,
                    ...(action.action ===
                    InfinityEnums.OperatorUserActionType.Create
                      ? { attributes }
                      : {}),
                  }
                : null
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Deactivate
            ) {
              verb = t('OperatorUserActions.Deactivated')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Activate
            ) {
              verb = t('OperatorUserActions.Activated')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.OCPP
            ) {
              const stationAssociation = action.associations?.find(
                (assoc) => assoc.model === InfinityEnums.Models.ChargePoint,
              )
              const stationTarget = action.targets?.find(
                (target) => target.model === InfinityEnums.Models.ChargePoint,
              )
              const ocppActionTypeKey = `${action.ocppActionType}`

              if (
                action.ocppActionType ===
                InfinityEnums.OperatorUserOcppActionType.ChangeConfiguration
              ) {
                verb = t('OperatorUserActions.Changed')
                if (stationAssociation?.attributes?.[0]) {
                  phrase = `${userAction} ${verb} ${
                    stationAssociation.attributes[0].name
                  } ${t('OperatorUserActions.To')} ${
                    stationAssociation.attributes[0].newValue
                  }`
                }
              } else {
                verb = t('OperatorUserActions.Preformed')
                phrase = `${userAction} ${verb} ${t(ocppActionTypeKey)} ${t(
                  'OperatorUserActions.On',
                )} ${
                  stationTarget?.reference ||
                  stationAssociation?.reference ||
                  ''
                }`
              }

              return phrase
                ? {
                    phrase,
                    createdAt,
                    details: action.details,
                    verb: action.action,
                  }
                : null
            }
            phrase = `${userAction} ${verb} ${
              model ? model + ' ' : ''
            }${subject}`
            return phrase
              ? {
                  phrase,
                  createdAt,
                  verb: action.action,
                  ...(action.action ===
                    InfinityEnums.OperatorUserActionType.Create ||
                  action.action === InfinityEnums.OperatorUserActionType.Invite
                    ? { attributes }
                    : {}),
                }
              : null
          })
        })
        .flat()
        .filter(
          (action) =>
            action &&
            action.verb !== InfinityEnums.OperatorUserActionType.Invite,
        )
    } else {
      return operatorUserActions
        ?.map((action) => {
          return action.targets?.map((target) => {
            let userAction
            let subject
            let model = ''
            let phrase = ''
            let verb = ''
            let createdAt = moment(action.createdAt).format('MMM DD, YYYY LTS')
            let attributes = ''

            userAction = t('ChargePointDetails.EditingOperatorUser')
            if (
              (clickedOperatorUser &&
                Object.keys(clickedOperatorUser).length > 0) ||
              action.operatorUser.id !== operatorUser?.id
            )
              userAction = action.operatorUser.name

            subject = `${target.reference}`
            if (
              (userAction === t('ChargePointDetails.EditingOperatorUser') ||
                (clickedOperatorUser &&
                  Object.keys(clickedOperatorUser).length === 0)) &&
              target.id?.modelId == operatorUser?.id
            )
              subject = t('OperatorUserActions.AdminPronoun')

            model = `${target.model}:`
            if (
              target.model === InfinityEnums.Models.OperatorUser &&
              subject === t('OperatorUserActions.AdminPronoun')
            )
              model = ''

            if (action.action === InfinityEnums.OperatorUserActionType.Edit) {
              verb = t('OperatorUserActions.Changed')

              subject = `${target.reference}'s`
              if (
                (userAction === t('ChargePointDetails.EditingOperatorUser') ||
                  (clickedOperatorUser &&
                    Object.keys(clickedOperatorUser).length === 0)) &&
                target.id?.modelId == operatorUser?.id
              )
                subject = t('OperatorUserActions.AdminPronoun')

              const fields = target.attributes || []

              let newValues = {}
              let displayPopOver = false
              let fieldName
              const formattedFields = fields
                ?.map((field) => {
                  if (field.deletedValueReference) {
                    verb = t('OperatorUserActions.Removed')
                    phrase = `${userAction} ${verb} ${
                      field.deletedValueReference
                    } ${t('OperatorUserActions.from')} ${subject} ${field.name}`
                    return null
                  }
                  if (
                    typeof field.newValue === 'object' &&
                    (field.name === 'emails' || field.name === 'trackedStatus')
                  ) {
                    displayPopOver = true

                    newValues[field.name] = field.newValue
                    if (field.name === 'trackedStatus') {
                      fieldName = field.name
                    }
                  }
                  if (field.newValue === null) {
                    field.newValue = ''
                  }
                  if (
                    Array.isArray(field.newValue) ||
                    field.newValue === '' ||
                    field.newValue?.length > 30
                  ) {
                    return field.name
                  }
                  if (typeof field.newValue === 'number') {
                    field.newValue = field.newValue.toFixed(2)
                  }

                  if (
                    typeof field.newValue === 'string' &&
                    /^[a-fA-F0-9]{24}$/.test(field.newValue)
                  ) {
                    const association = action.associations.find(
                      (item) =>
                        item.id.modelId.toString() ===
                        field.newValue.toString(),
                    )
                    let newValue = association
                      ? association.reference
                      : field.newValue
                    if (newValue?.length > 20) {
                      newValue = newValue.slice(0, 20) + '...'
                    }
                    return `${field.name} to ${newValue}`
                  }
                  if (
                    typeof field.newValue === 'object' &&
                    field.newValue !== null
                  ) {
                    let objectValue = JSON.stringify(field.newValue)
                    if (objectValue.length > 20) {
                      objectValue = objectValue.slice(0, 20) + '...'
                    }
                    return `${field.name} to ${objectValue}`
                  }
                  let displayValue = field.newValue
                  if (
                    typeof displayValue === 'string' &&
                    displayValue?.length > 20
                  ) {
                    displayValue = displayValue.slice(0, 20) + '...'
                  }
                  if (displayPopOver) {
                    return `${field.name}`
                  }
                  return `${field.name}  to ${displayValue}`
                })
                .join(', ')

              if (verb === t('OperatorUserActions.Changed')) {
                phrase = `${userAction} ${verb} ${
                  model ? model + ' ' : ''
                }${subject} ${formattedFields}`
              }

              return phrase
                ? {
                    phrase,
                    createdAt,
                    verb: action.action,
                    newValue: displayPopOver ? newValues : {},
                    displayPopOver,
                    ...(fieldName ? { fieldName } : {}),
                  }
                : null
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Delete
            ) {
              verb = t('OperatorUserActions.Deleted')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Create ||
              action.action === InfinityEnums.OperatorUserActionType.Invite
            ) {
              verb = t('OperatorUserActions.Created')
              if (action.action === InfinityEnums.OperatorUserActionType.Invite)
                verb = t('OperatorUserActions.Invited')
              attributes = target.attributes?.map((field) => {
                let newValue = field.newValue
                if (Array.isArray(newValue)) {
                  if (newValue.length > 0 && newValue[0].priceComponents) {
                    newValue = newValue
                      .map((element, index) => {
                        const types = element.priceComponents
                          .map((pc) => pc.type)
                          .join(', ')
                        return ` ${types}`
                      })
                      .join(' &')
                  } else {
                    newValue = newValue?.map((item) => {
                      if (
                        typeof item === 'string' &&
                        /^[a-fA-F0-9]{24}$/.test(item)
                      ) {
                        const association = action.associations.find(
                          (assoc) =>
                            assoc.id.modelId.toString() === item.toString(),
                        )
                        return association ? association.reference : item
                      }
                      return item
                    })
                  }
                } else if (typeof newValue === 'object' && newValue !== null) {
                  newValue = Object.entries(newValue).reduce(
                    (acc, [key, value]) => {
                      if (
                        typeof value === 'string' &&
                        /^[a-fA-F0-9]{24}$/.test(value)
                      ) {
                        const association = action.associations.find(
                          (assoc) =>
                            assoc.id.modelId.toString() === value.toString(),
                        )
                        acc[key] = association ? association.reference : value
                      } else {
                        acc[key] = value
                      }
                      return acc
                    },
                    {},
                  )
                } else if (
                  typeof newValue === 'string' &&
                  moment(newValue, moment.ISO_8601, true).isValid()
                ) {
                  newValue = moment(newValue).format('MMM DD, YYYY LTS')
                } else if (
                  newValue &&
                  typeof newValue === 'string' &&
                  /^[a-fA-F0-9]{24}$/.test(field.newValue) &&
                  action.associations
                ) {
                  const association = action.associations.find(
                    (item) =>
                      item.id.modelId.toString() === field.newValue.toString(),
                  )
                  if (association) {
                    newValue = association.reference
                  }
                }

                return {
                  name: field.name,
                  newValue: newValue,
                }
              })
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Deactivate
            ) {
              verb = t('OperatorUserActions.Deactivated')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Activate
            ) {
              verb = t('OperatorUserActions.Activated')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Unblock
            ) {
              verb = t('OperatorUserActions.Unblocked')
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.Move
            ) {
              verb = t('OperatorUserActions.Moved')
              const stationAssociation = action.associations?.find(
                (assoc) => assoc.model === InfinityEnums.Models.Station,
              )
              if (stationAssociation) {
                phrase = `${userAction} ${verb} ${target.model}:  ${
                  target.reference
                } ${t('OperatorUserActions.To')} ${stationAssociation.model}: ${
                  stationAssociation.reference
                }`
              } else {
                phrase = `${userAction} ${verb} ${
                  model ? model + ' ' : ''
                }${subject}`
              }

              return phrase
                ? {
                    phrase,
                    createdAt,
                    verb: action.action,
                  }
                : null
            } else if (
              action.action === InfinityEnums.OperatorUserActionType.OCPP
            ) {
              const stationAssociation = action.associations?.find(
                (assoc) => assoc.model === InfinityEnums.Models.ChargePoint,
              )
              const stationTarget = action.targets?.find(
                (target) => target.model === InfinityEnums.Models.ChargePoint,
              )
              const ocppActionTypeKey = `${action.ocppActionType}`

              if (
                action.ocppActionType ===
                InfinityEnums.OperatorUserOcppActionType.ChangeConfiguration
              ) {
                verb = t('OperatorUserActions.Changed')
                if (stationAssociation?.attributes?.[0]) {
                  phrase = `${userAction} ${verb} ${
                    stationAssociation.attributes[0].name
                  } ${t('OperatorUserActions.To')} ${
                    stationAssociation.attributes[0].newValue
                  }`
                }
              } else {
                verb = t('OperatorUserActions.Preformed')
                phrase = `${userAction} ${verb} ${t(ocppActionTypeKey)} ${t(
                  'OperatorUserActions.On',
                )} ${
                  stationTarget?.reference ||
                  stationAssociation?.reference ||
                  ''
                }`
              }
              return phrase
                ? {
                    phrase,
                    createdAt,
                    details: action.details,
                    verb: action.action,
                  }
                : null
            }
            phrase = `${userAction} ${verb} ${
              model ? model + ' ' : ''
            }${subject}`
            return phrase
              ? {
                  phrase,
                  createdAt,
                  verb: action.action,
                  ...(action.action ===
                    InfinityEnums.OperatorUserActionType.Create ||
                  action.action ===
                    InfinityEnums.OperatorUserActionType.Invite ||
                  action.action === InfinityEnums.OperatorUserActionType.Edit
                    ? { attributes }
                    : {}),
                }
              : null
          })
        })
        .flat()
        .filter(Boolean)
    }
  }, [operatorUserActions])
  const Footer = () => {
    return paginationOffset &&
      flattenedActions &&
      flattenedActions.length !== 0 ? (
      <div>
        {failedRequest ? (
          <div className={Styles.IconDiv}>
            <Waypoint onEnter={loadMoreData} />
            <div className={Styles.ReportIconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
              <div className={Styles.TextDiv}>
                <IFButton
                  size="sm"
                  color={Colors.red}
                  text={t('IFTable.TryAgain')}
                  onClick={() => {
                    handleRetry()
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={Styles.LoadingMore}>
            <Box className={Styles.SkeletonLineDiv}>
              <Box
                className={Styles.SkeletonLine}
                sx={{
                  backgroundColor: Colors.ActionLine,
                }}
              />
            </Box>
            <OperatorUserActionSkeleton />
            <Box className={Styles.SkeletonLineDiv}>
              <Box
                className={Styles.SkeletonLine}
                sx={{
                  backgroundColor: Colors.ActionLine,
                }}
              />
            </Box>
            <OperatorUserActionSkeleton />
          </div>
        )}
      </div>
    ) : null
  }
  useEffect(() => {
    if (
      fetchOperatorUserActionsRequestState === RequestState.ERROR_0_NETWORK ||
      fetchOperatorUserActionsRequestState ===
        RequestState.ERROR_400_OCCURRED ||
      fetchOperatorUserActionsRequestState ===
        RequestState.ERROR_401_OCCURRED ||
      fetchOperatorUserActionsRequestState ===
        RequestState.ERROR_403_OCCURRED ||
      fetchOperatorUserActionsRequestState ===
        RequestState.ERROR_409_OCCURRED ||
      fetchOperatorUserActionsRequestState ===
        RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchOperatorUserActionsRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchOperatorUserActionsRequestState])

  const renderAttributeValue = (value, keyPrefix = '', level = 0) => {
    if (value === '' || value === null || value === undefined) return null
    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <div
          className={Styles.LabelValueObject}
          style={{ marginLeft: `${level * 4}px` }}
        >
          {Object.entries(value).map(([key, val]) => (
            <div key={keyPrefix + key} className={Styles.DetailItem}>
              <div className={Styles.LabelTitleObject}>
                <IFText style={{ color: Colors.DetailPanel }}>{key}</IFText>
              </div>
              <div className={Styles.LabelValue}>
                {renderAttributeValue(val, keyPrefix + key + '.', level + 1)}
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (Array.isArray(value)) {
      return (
        <IFText
          className={Styles.LabelValueArray}
          style={{
            color: Colors.DetailPanel,
          }}
        >
          {value.join(', ')}
        </IFText>
      )
    }

    return (
      <IFText style={{ color: Colors.DetailPanel }}>{String(value)}</IFText>
    )
  }
  return (
    <div
      className={Styles.OperatorUserActionList}
      style={{ height: tablesHeight }}
    >
      {fetchOperatorUserActionsRequestState === RequestState.LOADING &&
      paginationOffset === 0 ? (
        <>
          {[...Array(12)].map((_, index) => (
            <>
              {index !== 0 && (
                <Box className={Styles.SkeletonLineDiv}>
                  <Box
                    className={Styles.SkeletonLine}
                    sx={{
                      backgroundColor: Colors.grey,
                    }}
                  />
                </Box>
              )}
              <OperatorUserActionSkeleton />
            </>
          ))}
        </>
      ) : flattenedActions && flattenedActions.length === 0 ? (
        <div className={Styles.NoRecord}>
          <IFText>{t('OperatorUserActions.OperatorUserActionNoRecord')}</IFText>
        </div>
      ) : (
        <Timeline
          className={Styles.timeline}
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}
        >
          <div
            ref={rootRef}
            data-overlayscrollbars=""
            className={Styles.VirtuosoDiv}
          >
            <Virtuoso
              scrollerRef={setScroller}
              ref={virtuosoRef}
              className={Styles.Scroll}
              data={flattenedActions}
              endReached={() => {
                if (
                  paginationOffset &&
                  flattenedActions &&
                  flattenedActions.length > 0
                ) {
                  loadMoreData()
                }
              }}
              increaseViewportBy={480}
              itemContent={(index, action) => (
                <div key={`ActionItem ${index}`}>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot
                        className={Styles.TimeLineDot}
                        style={{
                          backgroundColor: Colors.ActionDotBackground,
                        }}
                      >
                        {action.verb ===
                        InfinityEnums.OperatorUserActionType.Edit ? (
                          <>
                            <EditOutlinedIcon
                              className={Styles.Icons}
                              style={{
                                color: Colors.black,
                              }}
                              aria-owns={
                                openPopoverIndex === index
                                  ? 'mouse-over-popover'
                                  : undefined
                              }
                              aria-haspopup="true"
                              onMouseEnter={(event) =>
                                handlePopoverOpen(event, index)
                              }
                              onMouseLeave={handlePopoverClose}
                            />
                            {action.displayPopOver && (
                              <Popover
                                id="mouse-over-popover"
                                sx={{ pointerEvents: 'none', maxWidth: 600 }}
                                open={openPopoverIndex === index}
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                }}
                                transformOrigin={{
                                  vertical: 'top',
                                  horizontal: 'left',
                                }}
                                onClose={handlePopoverClose}
                                disableRestoreFocus
                              >
                                <div className={Styles.Popover}>
                                  {action.newValue &&
                                    typeof action.newValue === 'object' && (
                                      <div className={Styles.EmailDetailsItems}>
                                        {action.newValue.emails?.added?.length >
                                          0 && (
                                          <div className={Styles.DetailItem}>
                                            <div className={Styles.LabelTitle}>
                                              <IFText
                                                style={{
                                                  color: Colors.DetailPanel,
                                                }}
                                              >
                                                {t('OperatorUserActions.Added')}
                                              </IFText>
                                            </div>
                                            <div className={Styles.LabelValue}>
                                              <IFText
                                                style={{
                                                  color: Colors.DetailPanel,
                                                }}
                                              >
                                                {action.newValue.emails.added.join(
                                                  ', ',
                                                )}
                                              </IFText>
                                            </div>
                                          </div>
                                        )}
                                        {action.newValue.emails?.removed
                                          ?.length > 0 && (
                                          <div className={Styles.DetailItem}>
                                            <div className={Styles.LabelTitle}>
                                              <IFText
                                                style={{
                                                  color: Colors.DetailPanel,
                                                }}
                                              >
                                                {t(
                                                  'OperatorUserActions.Removed',
                                                )}
                                              </IFText>
                                            </div>
                                            <div className={Styles.LabelValue}>
                                              <IFText
                                                style={{
                                                  color: Colors.DetailPanel,
                                                }}
                                              >
                                                {action.newValue.emails.removed.join(
                                                  ', ',
                                                )}
                                              </IFText>
                                            </div>
                                          </div>
                                        )}
                                        {Array.isArray(
                                          action.newValue.trackedStatus,
                                        ) &&
                                          action.newValue.trackedStatus.length >
                                            0 && (
                                            <div className={Styles.DetailItem}>
                                              <div
                                                className={Styles.LabelTitle}
                                              >
                                                <IFText
                                                  style={{
                                                    color: Colors.DetailPanel,
                                                  }}
                                                >
                                                  {action.fieldName}
                                                </IFText>
                                              </div>
                                              <div
                                                className={Styles.LabelValue}
                                              >
                                                <IFText
                                                  style={{
                                                    color: Colors.DetailPanel,
                                                  }}
                                                >
                                                  {action.newValue.trackedStatus.join(
                                                    ', ',
                                                  )}
                                                </IFText>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                </div>
                              </Popover>
                            )}
                          </>
                        ) : action.verb ===
                          InfinityEnums.OperatorUserActionType.Delete ? (
                          <DeleteOutlineIcon
                            className={Styles.Icons}
                            style={{
                              color: Colors.black,
                            }}
                          />
                        ) : action.verb ===
                            InfinityEnums.OperatorUserActionType.Create ||
                          action.verb ===
                            InfinityEnums.OperatorUserActionType.Invite ? (
                          <>
                            <AddIcon
                              aria-owns={
                                openPopoverIndex === index
                                  ? 'mouse-over-popover'
                                  : undefined
                              }
                              aria-haspopup="true"
                              onMouseEnter={(event) =>
                                handlePopoverOpen(event, index)
                              }
                              onMouseLeave={handlePopoverClose}
                              className={Styles.Icons}
                              style={{
                                color: Colors.black,
                              }}
                            />

                            <Popover
                              id="mouse-over-popover"
                              sx={{ pointerEvents: 'none' }}
                              open={openPopoverIndex === index}
                              anchorEl={anchorEl}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                              }}
                              onClose={handlePopoverClose}
                              disableRestoreFocus
                            >
                              <div className={Styles.Popover}>
                                {action.attributes?.map(
                                  ({ name, newValue }) => {
                                    if (
                                      newValue === '' ||
                                      newValue === null ||
                                      newValue === undefined
                                    )
                                      return null
                                    return (
                                      <div
                                        key={name}
                                        className={Styles.DetailItem}
                                      >
                                        <div className={Styles.LabelTitle}>
                                          <IFText
                                            style={{
                                              color: Colors.DetailPanel,
                                            }}
                                          >
                                            {name}
                                          </IFText>
                                        </div>
                                        <div className={Styles.LabelValue}>
                                          {renderAttributeValue(
                                            newValue,
                                            name + '.',
                                          )}
                                        </div>
                                      </div>
                                    )
                                  },
                                )}
                              </div>
                            </Popover>
                          </>
                        ) : action.verb ===
                          InfinityEnums.OperatorUserActionType.Activate ? (
                          <ToggleOnOutlinedIcon
                            className={Styles.Icons}
                            style={{
                              color: Colors.black,
                            }}
                          />
                        ) : action.verb ===
                          InfinityEnums.OperatorUserActionType.Deactivate ? (
                          <ToggleOffOutlinedIcon
                            className={Styles.Icons}
                            style={{
                              color: Colors.black,
                            }}
                          />
                        ) : action.verb ===
                          InfinityEnums.OperatorUserActionType.OCPP ? (
                          <>
                            <SettingsRemoteIcon
                              aria-owns={
                                openPopoverIndex === index
                                  ? 'mouse-over-popover'
                                  : undefined
                              }
                              aria-haspopup="true"
                              onMouseEnter={(event) =>
                                handlePopoverOpen(event, index)
                              }
                              onMouseLeave={handlePopoverClose}
                              className={Styles.Icons}
                              style={{
                                color: Colors.black,
                              }}
                            />
                            <Popover
                              id="mouse-over-popover"
                              sx={{ pointerEvents: 'none' }}
                              open={openPopoverIndex === index}
                              anchorEl={anchorEl}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                              }}
                              onClose={handlePopoverClose}
                              disableRestoreFocus
                            >
                              <div className={Styles.Popover}>
                                <div className={Styles.DetailItem}>
                                  <div className={Styles.LabelTitleOCPP}>
                                    <IFText
                                      style={{
                                        color: Colors.DetailPanel,
                                      }}
                                    >
                                      {t('ChargePointDetails.Details')}
                                    </IFText>
                                  </div>
                                  <div className={Styles.LabelValue}>
                                    <IFText>{action.details}</IFText>
                                  </div>
                                </div>
                              </div>
                            </Popover>
                          </>
                        ) : action.verb ===
                          InfinityEnums.OperatorUserActionType.Move ? (
                          <SwapHorizIcon
                            className={Styles.Icons}
                            style={{
                              color: Colors.black,
                            }}
                          />
                        ) : action.verb ===
                          InfinityEnums.OperatorUserActionType.Unblock ? (
                          <LockOpenOutlinedIcon
                            className={Styles.Icons}
                            style={{
                              color: Colors.black,
                            }}
                          />
                        ) : null}
                      </TimelineDot>
                      {index !== flattenedActions.length - 1 && (
                        <TimelineConnector />
                      )}
                    </TimelineSeparator>

                    <TimelineContent className={Styles.TimeLineContent}>
                      <div>
                        <IFText
                          className={Styles.CountryName}
                          style={{
                            color: Colors.text,
                          }}
                        >
                          {action.phrase}
                        </IFText>
                      </div>
                      <div className={Styles.DateCountryDiv}>
                        <IFText
                          className={Styles.DateName}
                          style={{
                            color: Colors.gray,
                          }}
                        >
                          {action.createdAt}
                        </IFText>
                      </div>
                    </TimelineContent>
                  </TimelineItem>
                </div>
              )}
              components={{ Footer: Footer }}
            />
          </div>
        </Timeline>
      )}
    </div>
  )
}
export default OperatorUserActionList
